import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  AssignmentInd as AssignmentIndIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import Navbar from './Navbar';
import { useNavigate } from 'react-router-dom';

const Clients = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createClientDialog, setCreateClientDialog] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
  });
  
  // New state for showing password after client creation
  const [createdClientCredentials, setCreatedClientCredentials] = useState(null);

  // New state for agent assignment
  const [assignAgentDialog, setAssignAgentDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [agencyAgents, setAgencyAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [clientAgents, setClientAgents] = useState({});

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Load clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch clients
  const fetchClients = async () => {
    setLoading(true);
    try {
      console.log('Fetching clients...');
      const token = localStorage.getItem('token');
      
      // Use the correct endpoint based on user role
      const endpoint = user.role === 'admin' ? 
        `${API_URL}/api/admin/organizations` : 
        `${API_URL}/api/agency/clients`;
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Clients fetched successfully:', data);
        
        // Handle different response formats based on endpoint
        if (user.role === 'admin') {
          // Filter to show only client organizations for admin
          const clientOrgs = data.organizations ? 
            data.organizations.filter(org => org.type === 'client') : [];
          setClients(clientOrgs);
          
          // Fetch agents for each client
          clientOrgs.forEach(client => {
            fetchClientAgents(client.id);
          });
        } else {
          // For agency users, the /api/agency/clients endpoint already returns clients
          setClients(data.clients || []);
          
          // Fetch agents for each client
          (data.clients || []).forEach(client => {
            fetchClientAgents(client.id);
          });
        }
      } else {
        console.error('Failed to fetch clients:', response.status, await response.text());
        showSnackbar('Failed to fetch clients', 'error');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      showSnackbar('Error loading clients', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch agents for a specific client
  const fetchClientAgents = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations/${clientId}/agents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`Agents for client ${clientId}:`, data);
        setClientAgents(prev => ({
          ...prev,
          [clientId]: data.agents || []
        }));
      } else {
        console.error(`Failed to fetch agents for client ${clientId}:`, response.status, await response.text());
      }
    } catch (error) {
      console.error(`Error fetching agents for client ${clientId}:`, error);
    }
  };

  // Fetch agency's agents for assignment
  const fetchAgencyAgents = async () => {
    try {
      console.log('Fetching agency agents...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/agents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Agency agents fetched successfully:', data);
        
        // Get agents belonging to the agency
        const agents = Array.isArray(data) ? data : (data.agents || []);
        setAgencyAgents(agents);
        
        // Pre-select agents already assigned to this client
        if (selectedClient && clientAgents[selectedClient.id]) {
          const clientAgentIds = clientAgents[selectedClient.id].map(agent => agent.id);
          setSelectedAgents(clientAgentIds);
        }
      } else {
        console.error('Failed to fetch agency agents:', response.status, await response.text());
        showSnackbar('Failed to fetch agents', 'error');
      }
    } catch (error) {
      console.error('Error fetching agency agents:', error);
      showSnackbar('Error loading agents', 'error');
    }
  };

  // Create a new client
  const handleCreateClient = async () => {
    if (!newClient.name.trim() || !newClient.email.trim()) {
      showSnackbar('All fields are required', 'error');
      return;
    }

    try {
      console.log(`Creating new client: ${newClient.name}...`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newClient.name.trim(),
          email: newClient.email.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Client created successfully:', data);
        fetchClients(); // Refresh the client list
        setCreateClientDialog(false);
        setNewClient({
          name: '',
          email: '',
        });
        setCreatedClientCredentials(data);
        showSnackbar('Client created successfully', 'success');
      } else {
        console.error('Failed to create client:', response.status, await response.text());
        showSnackbar('Failed to create client', 'error');
      }
    } catch (error) {
      console.error('Error creating client:', error);
      showSnackbar('Error creating client', 'error');
    }
  };

  // Open agent assignment dialog
  const openAssignAgentDialog = (client) => {
    setSelectedClient(client);
    setSelectedAgents([]); // Reset selected agents
    setAssignAgentDialog(true);
    fetchAgencyAgents(); // Load agency's agents
  };

  // Handle agent checkbox toggle
  const handleAgentToggle = (agentId) => {
    setSelectedAgents(prev => {
      if (prev.includes(agentId)) {
        return prev.filter(id => id !== agentId);
      } else {
        return [...prev, agentId];
      }
    });
  };

  // Assign agents to client
  const handleAssignAgents = async () => {
    if (!selectedClient) return;
    
    setAssignmentLoading(true);
    try {
      console.log(`Assigning agents to client ${selectedClient.id}:`, selectedAgents);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations/${selectedClient.id}/assign-agents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          agent_ids: selectedAgents
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Agents assigned successfully:', data);
        setAssignAgentDialog(false);
        
        // Refresh client's agents
        fetchClientAgents(selectedClient.id);
        
        showSnackbar(`Successfully assigned ${data.assigned_agent_ids.length} agents to ${selectedClient.name}`, 'success');
      } else {
        console.error('Failed to assign agents:', response.status, await response.text());
        showSnackbar('Failed to assign agents', 'error');
      }
    } catch (error) {
      console.error('Error assigning agents:', error);
      showSnackbar('Error assigning agents', 'error');
    } finally {
      setAssignmentLoading(false);
    }
  };

  // Navigate to client details page
  const handleViewClientDetails = (clientId) => {
    navigate(`/client-details/${clientId}`);
  };

  // Show snackbar message
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ pt: 4, pb: 2 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4" component="h1" gutterBottom>
              Client Management
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setCreateClientDialog(true)}
            >
              New Client
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : clients.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No clients found. Create your first client to get started.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {clients.map(client => (
            <Grid item xs={12} sm={6} md={4} key={client.id}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="div">
                      {client.name}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography color="text.secondary" gutterBottom>
                    <strong>Created:</strong> {formatDate(client.created_at)}
                  </Typography>
                  
                  {/* Show assigned agents */}
                  {clientAgents[client.id] && clientAgents[client.id].length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Assigned Agents ({clientAgents[client.id].length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {clientAgents[client.id].map(agent => (
                          <Chip
                            key={agent.id}
                            label={agent.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<PersonIcon />}
                    onClick={() => handleViewClientDetails(client.id)}
                  >
                    View Details
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<AssignmentIndIcon />}
                    onClick={() => openAssignAgentDialog(client)}
                    color="secondary"
                  >
                    Assign Agents
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Client Dialog */}
      <Dialog open={createClientDialog} onClose={() => setCreateClientDialog(false)}>
        <DialogTitle>Create New Client</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Client Name"
            fullWidth
            required
            value={newClient.name}
            onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Client Email"
            type="email"
            fullWidth
            required
            value={newClient.email}
            onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will create a new client organization and an admin user for that client.
            A secure password will be automatically generated and sent to the client's email.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateClientDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateClient} variant="contained" color="primary">
            Create Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Agents Dialog */}
      <Dialog 
        open={assignAgentDialog} 
        onClose={() => setAssignAgentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assign Agents to {selectedClient?.name}
        </DialogTitle>
        <DialogContent>
          {assignmentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : agencyAgents.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No agents available for assignment. Create agents first.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the agents you want to assign to this client. Agents will only be accessible to this client organization.
              </Typography>
              <List>
                {agencyAgents.map((agent) => (
                  <ListItem key={agent.id} divider>
                    <ListItemText 
                      primary={agent.name} 
                      secondary={`Type: ${agent.agent_type || 'Unknown'} • Status: ${agent.status || 'Unknown'}`}
                    />
                    <ListItemSecondaryAction>
                      <Checkbox
                        edge="end"
                        onChange={() => handleAgentToggle(agent.id)}
                        checked={selectedAgents.includes(agent.id)}
                        color="primary"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignAgentDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignAgents} 
            variant="contained" 
            color="primary"
            disabled={assignmentLoading || selectedAgents.length === 0}
            startIcon={<CheckIcon />}
          >
            Assign Selected Agents
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Clients; 